// frontend/src/components/CodeTable.jsx
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { format } from "date-fns";

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {[160, 140, 200, 72, 60, 96, 100].map((w, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-3.5 bg-gray-200 rounded" style={{ width: w }} />
        </td>
      ))}
    </tr>
  );
}

function ActiveBadge({ active }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
      }`}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}
ActiveBadge.propTypes = { active: PropTypes.bool.isRequired };

/** Human labels for the backend `qr_type` discriminator values. */
const TYPE_LABELS = {
  url: "URL",
  wifi: "WiFi",
  vcard: "vCard",
  email: "Email",
  phone: "Phone",
  plain_text: "Text",
  pdf: "PDF",
  video: "Video",
  menu: "Menu",
  feedback: "Feedback",
  app_store: "App download",
  location: "Location",
  event: "Event",
  coupon: "Coupon",
  social_links: "Social links",
};

/** TypeBadge — small pill showing a code's QR type (URL / WiFi / vCard …). */
function TypeBadge({ type }) {
  if (!type) return null;
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-blue-50 text-blue-600">
      {TYPE_LABELS[type] || type}
    </span>
  );
}
TypeBadge.propTypes = { type: PropTypes.string };
TypeBadge.defaultProps = { type: undefined };

function ActionBtn({ label, onClick, className, children }) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors ${className}`}
    >
      {children}
    </button>
  );
}
ActionBtn.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};
ActionBtn.defaultProps = { className: "" };

/** CopyButton — copies text to clipboard, shows brief "Copied!" confirmation. */
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleClick = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — silently ignore */
    }
  };
  return (
    <button
      onClick={handleClick}
      title="Copy token"
      aria-label="Copy token"
      className="ml-1.5 p-1 rounded text-gray-300 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
    >
      {copied ? (
        <svg
          className="w-3.5 h-3.5 text-green-500"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      ) : (
        <svg
          className="w-3.5 h-3.5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
        >
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"
          />
        </svg>
      )}
    </button>
  );
}
CopyButton.propTypes = { text: PropTypes.string.isRequired };

/** RowMenu — ⋮ dropdown shown on mobile instead of icon buttons. */
function RowMenu({ item, onEdit, onDelete, onDownloadQr, onToggleActive }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const act = (fn) => {
    setOpen(false);
    fn();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Actions"
        className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="1.5" />
          <circle cx="12" cy="12" r="1.5" />
          <circle cx="12" cy="19" r="1.5" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1 w-40 rounded-lg border border-gray-200 bg-white shadow-lg py-1 text-sm">
          <button
            onClick={() => act(() => onEdit(item))}
            className="w-full text-left px-4 py-2 hover:bg-gray-50 text-gray-700"
          >
            Edit
          </button>
          <button
            onClick={() => act(() => onToggleActive(item))}
            className="w-full text-left px-4 py-2 hover:bg-gray-50 text-gray-700"
          >
            {item.is_active ? "Deactivate" : "Activate"}
          </button>
          <button
            onClick={() => act(() => onDownloadQr(item.code))}
            className="w-full text-left px-4 py-2 hover:bg-gray-50 text-gray-700"
          >
            Download QR
          </button>
          <button
            onClick={() => act(() => onDelete(item))}
            className="w-full text-left px-4 py-2 hover:bg-red-50 text-red-600"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
RowMenu.propTypes = {
  item: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDownloadQr: PropTypes.func.isRequired,
  onToggleActive: PropTypes.func.isRequired,
};

const COLS = [
  "Name / Label",
  "Token",
  "Destination",
  "Status",
  "Scans",
  "Created",
  "Actions",
];

function CodeTable({
  items,
  loading,
  onEdit,
  onDelete,
  onDownloadQr,
  onToggleActive,
  selectable,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allSelected,
}) {
  // The select column widens the table by one cell — keep colSpan in sync.
  const colCount = COLS.length + (selectable ? 1 : 0);
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            {selectable && (
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  aria-label="Select all on this page"
                  checked={allSelected}
                  onChange={onToggleSelectAll}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
            )}
            {COLS.map((col) => (
              <th
                key={col}
                className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading &&
            Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}

          {!loading && items.length === 0 && (
            <tr>
              <td
                colSpan={colCount}
                className="px-4 py-12 text-center text-gray-400 text-sm"
              >
                No QR codes found. Create one to get started.
              </td>
            </tr>
          )}

          {!loading &&
            items.map((item) => (
              <tr
                key={item.id}
                className={`transition-colors ${
                  selectable && selectedIds.includes(item.id)
                    ? "bg-blue-50/60"
                    : "hover:bg-gray-50"
                }`}
              >
                {selectable && (
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label={`Select ${item.code}`}
                      checked={selectedIds.includes(item.id)}
                      onChange={() => onToggleSelect(item.id)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                )}
                {/* Name — falls back to a dash if not set; QR type shown as a badge */}
                <td
                  className="px-4 py-3 text-gray-800 max-w-[12rem]"
                  title={item.name || ""}
                >
                  <div className="flex items-center gap-2">
                    <span className="truncate">
                      {item.name ? (
                        <Link
                          to={`/codes/${item.code}`}
                          className="font-medium text-gray-800 hover:text-blue-700 hover:underline"
                          title="View statistics"
                        >
                          {item.name}
                        </Link>
                      ) : (
                        <span className="text-gray-400 italic text-xs">—</span>
                      )}
                    </span>
                    <TypeBadge type={item.qr_type} />
                  </div>
                </td>
                {/* Token — links to the code's stats page; copy button alongside */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center">
                    <Link
                      to={`/codes/${item.code}`}
                      className="font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline"
                      title="View statistics"
                    >
                      {item.code}
                    </Link>
                    <CopyButton text={item.code} />
                  </div>
                </td>
                <td
                  className="px-4 py-3 text-gray-600 max-w-xs truncate"
                  title={item.text}
                >
                  {item.text}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <ActiveBadge active={item.is_active} />
                </td>
                <td className="px-4 py-3 text-gray-600 tabular-nums">
                  {item.scan_count ?? "—"}
                </td>
                <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                  {format(new Date(item.created_at), "MMM d, yyyy")}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {/* Desktop: icon buttons */}
                  <div className="hidden sm:flex items-center gap-1">
                    <ActionBtn label="Edit" onClick={() => onEdit(item)}>
                      <svg
                        className="w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.828a4 4 0 01-2.828 1.172H7v-2a4 4 0 011.172-2.828L9 13z"
                        />
                      </svg>
                    </ActionBtn>
                    <ActionBtn
                      label={item.is_active ? "Deactivate" : "Activate"}
                      onClick={() => onToggleActive(item)}
                      className={
                        item.is_active
                          ? "hover:text-amber-600 hover:bg-amber-50"
                          : "hover:text-green-600 hover:bg-green-50"
                      }
                    >
                      {item.is_active ? (
                        // pause — deactivate
                        <svg
                          className="w-4 h-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={1.8}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      ) : (
                        // play — activate
                        <svg
                          className="w-4 h-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={1.8}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M14.752 11.168l-5.197-3.03A1 1 0 008 9.002v5.996a1 1 0 001.555.832l5.197-3.03a1 1 0 000-1.664z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      )}
                    </ActionBtn>
                    <ActionBtn
                      label="Download QR"
                      onClick={() => onDownloadQr(item.code)}
                    >
                      <svg
                        className="w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                        />
                      </svg>
                    </ActionBtn>
                    <ActionBtn
                      label="Delete"
                      onClick={() => onDelete(item)}
                      className="hover:text-red-600 hover:bg-red-50"
                    >
                      <svg
                        className="w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m-7 0a1 1 0 01-1-1V5a1 1 0 011-1h8a1 1 0 011 1v1a1 1 0 01-1 1H6z"
                        />
                      </svg>
                    </ActionBtn>
                  </div>
                  {/* Mobile: ⋮ dropdown */}
                  <div className="sm:hidden">
                    <RowMenu
                      item={item}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onDownloadQr={onDownloadQr}
                      onToggleActive={onToggleActive}
                    />
                  </div>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

CodeTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDownloadQr: PropTypes.func.isRequired,
  onToggleActive: PropTypes.func.isRequired,
  selectable: PropTypes.bool,
  selectedIds: PropTypes.arrayOf(PropTypes.number),
  onToggleSelect: PropTypes.func,
  onToggleSelectAll: PropTypes.func,
  allSelected: PropTypes.bool,
};
CodeTable.defaultProps = {
  selectable: false,
  selectedIds: [],
  onToggleSelect: () => {},
  onToggleSelectAll: () => {},
  allSelected: false,
};

export default CodeTable;
